import { ErrorType } from '@/utilities/errors/types';
import { SliceErrorState } from '../sliceError';

export type GeolocationSlice = {
  /** Increments whenever geolocation slice parts are reset (for effects tied to geo reload / soft reload). */
  geolocationResetRevision: number;
  geolocationContext: GeolocationContext;
  geolocationStatus: GeolocationStatus;
  geolocationError: SliceErrorState;
  /**
   * Browser permission pre-checks (geolocation + LNA + PLC port probe) have finished.
   * The GeoComply SDK must not load until this is `true`, otherwise Chrome permission
   * popups become unresponsive. Starts `false` and is flipped once the precheck resolves.
   */
  preChecksComplete: boolean;
  /** Number of NDBS-error retries the user has triggered (bounded by MAX_NDBS_RETRIES). */
  retryCount: number;

  setGeolocationContext: (newState: GeolocationContextPayload) => void;
  setGeolocationStatus: (newState: GeolocationStatusPayload) => void;
  setGeolocationPassed: (geolocationToken: string) => void;
  setGeolocationError: (newState: GeolocationErrorPayload) => void;
  setPreChecksComplete: (complete: boolean) => void;
  incrementRetryCount: () => void;
  resetGeolocationPart: (part: GeolocationStoreParts) => void;
  /** Clears launch failure state and bumps revision once before a programmatic re-locate. */
  resetGeolocationForRelocate: () => void;
  resetAllGeolocationParts: () => void;
};

export enum GeolocationStoreParts {
  geolocationContext = 'geolocationContext',
  geolocationStatus = 'geolocationStatus',
  geolocationError = 'geolocationError',
}

export type GeolocationContextPayload = Partial<GeolocationContext>;
export type GeolocationStatusPayload = Partial<GeolocationStatus>;
export type GeolocationErrorPayload = Partial<SliceErrorState>;

export type GeolocationContext = {
  geolocationToken: string | null;
};

export type GeolocationStatus = {
  geolocationFailed: boolean;
  geolocationRejected: boolean;
  geolocationSuccess: boolean;
  geolocationInProgress: boolean;
  previouslyVerified: boolean;
};

export enum GeolocationErrorType {
  General = 'generalError',
  PLC = 'plcError',
  NDBS = 'ndbsError',
  Validation = 'validationError',
  Retrying = 'retrying',
}

export type GeolocationError = {
  type: ErrorType.GEOLOCATION;
  payload: {
    type: GeolocationErrorType;
    message: string;
    errorCode?: number;
    region?: string;
    product?: string;
    troubleshooters?: unknown;
  };
}; // TO DO add error types from um if necessary maybe idk
